'use client';

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, ImageUp, Scan, Trash2 } from 'lucide-react';
import QRCode from 'react-qr-code';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';

import { cn } from '@/lib/utils';
import { UploadProgress } from '@/components/upload-progress';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import { isProblemPage, type BatchPageRecord } from '../types';
import { PHOTO_SCANNING_HASH } from '../constants';
import { useReorderPagesMutation } from '../services/photo-scanning.mutations';
import { FilterChip } from './filter-chip';
import {
  PageMatchLabel,
  PageMatchOverlay,
  resolvePageMatchBorderClass
} from './page-status-indicator';
import { QrPagePreviewDrawer } from './qr-page-preview-drawer';

interface QrPageGridProps {
  batchId: number;
  actualPages: BatchPageRecord[];
  containerId: string | null;
  gridFilter: 'all' | 'problems';
  onSetGridFilter: (filter: 'all' | 'problems') => void;
  readOnly?: boolean;
  onSelectPage: (pageId: number) => void;
  onDeletePage: (pageId: number) => void;
  onRescanPage: (pageId: number, file: File) => void | Promise<void>;
  scannerEnabled?: boolean;
  onHardwareRescan?: (pageId: number) => void;
  isDeletePending?: boolean;
  isRescanPending?: boolean;
  uploadProgress?: number | null;
  onRotatePage?: (pageId: number, delta: number) => void;
  isRotating?: boolean;
  imageVersions: Record<number, number>;
  // Rendered inline in this grid's own header row (next to the filter
  // chips) instead of floating as an absolute overlay, so it never collides
  // with the header — the document viewer has no equivalent header, so it
  // renders its own copy as a floating pill instead.
  viewToggle?: ReactNode;
}

/**
 * QR-first grid view of every scanned page — an alternative to the
 * document viewer + thumbnail rail. Each tile renders the page's printed
 * QR code (not the scanned document image) with the same match-status
 * overlay the thumbnail rail uses, supports the same per-page hover
 * actions, and can be dragged to reorder (auto-saved on drop). Clicking a
 * tile opens a full-image preview drawer. Only real pages are shown (no
 * blank/pending slots) — matches `RearrangePagesDialog`'s page list, not
 * the rail's PDF-style slot model.
 */
export function QrPageGrid({
  batchId,
  actualPages,
  containerId,
  gridFilter,
  onSetGridFilter,
  readOnly = false,
  onSelectPage,
  onDeletePage,
  onRescanPage,
  scannerEnabled = false,
  onHardwareRescan,
  isDeletePending = false,
  isRescanPending = false,
  uploadProgress,
  onRotatePage,
  isRotating = false,
  imageVersions,
  viewToggle
}: QrPageGridProps) {
  const t = useTranslations('photoScanning');
  const reorderMutation = useReorderPagesMutation();
  const [previewPageId, setPreviewPageId] = useState<number | null>(null);

  const {
    open: deletePageOpen,
    param: deletePageId,
    openWith: openDeletePage,
    onOpenChange: closeDeletePage
  } = useHashParamDialog(PHOTO_SCANNING_HASH.deletePage);

  const problemCount = useMemo(
    () => actualPages.filter(isProblemPage).length,
    [actualPages]
  );

  // Locally reordered copy so drag feedback is instant; resyncs whenever the
  // server order (actualPages) changes — including right after this grid's
  // own reorder mutation invalidates the batch query.
  const [orderedPages, setOrderedPages] =
    useState<BatchPageRecord[]>(actualPages);
  useEffect(() => {
    setOrderedPages(actualPages);
  }, [actualPages]);

  const visiblePages = useMemo(
    () =>
      gridFilter === 'problems'
        ? orderedPages.filter(isProblemPage)
        : orderedPages,
    [orderedPages, gridFilter]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const sortableIds = useMemo(
    () => visiblePages.map((p) => p.id),
    [visiblePages]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = orderedPages.findIndex((p) => p.id === active.id);
    const to = orderedPages.findIndex((p) => p.id === over.id);
    if (from < 0 || to < 0) return;
    const next = arrayMove(orderedPages, from, to);
    setOrderedPages(next);
    reorderMutation.mutate({ batchId, pageIds: next.map((p) => p.id) });
  };

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <div className='flex shrink-0 flex-wrap items-center justify-between gap-2 border-b px-3 py-2'>
        <span className='text-xs font-semibold text-slate-600 dark:text-slate-300'>
          {t('thumbnailRailTitle')}
        </span>
        <div className='flex items-center gap-2'>
          <div className='flex items-center gap-1'>
            <FilterChip
              active={gridFilter === 'all'}
              count={actualPages.length}
              label={t('filterAll')}
              tone='slate'
              onClick={() => onSetGridFilter('all')}
            />
            <FilterChip
              active={gridFilter === 'problems'}
              count={problemCount}
              label={t('filterProblems')}
              tone='amber'
              onClick={() => onSetGridFilter('problems')}
            />
          </div>
          {viewToggle}
        </div>
      </div>

      <UploadProgress value={uploadProgress} className='px-3 py-1.5' />

      {visiblePages.length === 0 ? (
        <div className='flex flex-1 items-center justify-center p-4 text-center text-xs text-slate-400 dark:text-slate-500'>
          {t('thumbnailRailEmpty')}
        </div>
      ) : (
        <div className='min-h-0 flex-1 overflow-y-auto p-3'>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
              <div className='grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7'>
                {visiblePages.map((page) => (
                  <GridTile
                    key={page.id}
                    page={page}
                    containerId={containerId}
                    readOnly={readOnly}
                    scannerEnabled={scannerEnabled}
                    isDeletePending={isDeletePending}
                    isRescanPending={isRescanPending}
                    onOpenPreview={() => {
                      onSelectPage(page.id);
                      setPreviewPageId(page.id);
                    }}
                    onRequestDelete={(id) => openDeletePage(String(id))}
                    onRescan={onRescanPage}
                    onHardwareRescan={onHardwareRescan}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      <QrPagePreviewDrawer
        batchId={batchId}
        pages={visiblePages}
        pageId={previewPageId}
        onOpenChange={setPreviewPageId}
        onRotatePage={onRotatePage}
        isRotating={isRotating}
        imageVersions={imageVersions}
      />

      <AlertDialog
        open={deletePageOpen}
        onOpenChange={(o) => {
          if (!o) closeDeletePage(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deletePageConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deletePageConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className='bg-rose-600 hover:bg-rose-700'
              onClick={() => {
                const pid = deletePageId != null ? Number(deletePageId) : null;
                if (pid != null) {
                  onDeletePage(pid);
                  closeDeletePage(false);
                }
              }}
            >
              {t('deletePage')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Sortable tile ───────────────────────────────────────────────────────
function GridTile({
  page,
  containerId,
  readOnly,
  scannerEnabled,
  isDeletePending,
  isRescanPending,
  onOpenPreview,
  onRequestDelete,
  onRescan,
  onHardwareRescan
}: {
  page: BatchPageRecord;
  containerId: string | null;
  readOnly: boolean;
  scannerEnabled: boolean;
  isDeletePending: boolean;
  isRescanPending: boolean;
  onOpenPreview: () => void;
  onRequestDelete: (pageId: number) => void;
  onRescan: (pageId: number, file: File) => void | Promise<void>;
  onHardwareRescan?: (pageId: number) => void;
}) {
  const t = useTranslations('photoScanning');
  const rescanInputRef = useRef<HTMLInputElement>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: page.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined
  };

  const label = `P-${page.sequence}`;
  const isProblem = isProblemPage(page);
  const showActions = !readOnly;
  // The container page's printed QR — same value the physical sticker
  // encodes, so the operator can visually compare the tile against the
  // physical page instead of reading a document thumbnail.
  const qrValue =
    containerId && containerId !== '—'
      ? `${containerId}-P${page.sequence}`
      : (page.barcode ?? page.page_number);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex flex-col items-center gap-1 pb-1',
        isDragging && 'opacity-60'
      )}
    >
      <div
        role='button'
        tabIndex={0}
        onClick={onOpenPreview}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenPreview();
          }
        }}
        aria-label={t('pageLabel', { n: label })}
        className={cn(
          'group relative flex aspect-[3/4] w-full cursor-pointer items-center justify-center overflow-hidden rounded-md border-2 bg-white shadow-sm transition-all hover:shadow-md dark:bg-slate-50',
          resolvePageMatchBorderClass(page)
        )}
      >
        <div className='flex h-full w-full items-center justify-center p-3'>
          <QRCode
            value={qrValue}
            viewBox='0 0 256 256'
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        <PageMatchOverlay page={page} />

        {/* Drag handle */}
        <button
          type='button'
          aria-label='Drag to reorder'
          onClick={(e) => e.stopPropagation()}
          className='absolute start-1 top-1 z-10 flex h-6 w-6 cursor-grab items-center justify-center rounded bg-white/90 text-slate-500 opacity-0 shadow transition-opacity group-hover:opacity-100 active:cursor-grabbing dark:bg-slate-900/80'
          {...attributes}
          {...listeners}
        >
          <GripVertical className='h-3.5 w-3.5' />
        </button>

        {showActions && (
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-center gap-1.5 bg-black/55 backdrop-blur-[1px] transition-opacity',
              isProblem
                ? 'opacity-100'
                : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'
            )}
          >
            {scannerEnabled && onHardwareRescan && (
              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  onHardwareRescan(page.id);
                }}
                className='inline-flex h-7 w-7 items-center justify-center rounded bg-white/90 text-violet-700 shadow hover:bg-white dark:bg-slate-900/80 dark:text-violet-200 dark:hover:bg-slate-900'
                title={t('rescanWithScanner')}
                aria-label={t('rescanWithScanner')}
              >
                <Scan className='h-4 w-4' />
              </button>
            )}
            {!scannerEnabled && (
              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  rescanInputRef.current?.click();
                }}
                disabled={isRescanPending}
                className='inline-flex h-7 w-7 items-center justify-center rounded bg-white/90 text-violet-700 shadow hover:bg-white disabled:opacity-40 dark:bg-slate-900/80 dark:text-violet-200 dark:hover:bg-slate-900'
                title={t('replaceImage')}
                aria-label={t('replaceImage')}
              >
                <ImageUp className='h-4 w-4' />
              </button>
            )}
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                onRequestDelete(page.id);
              }}
              disabled={isDeletePending}
              className='inline-flex h-7 w-7 items-center justify-center rounded bg-white/90 text-rose-600 shadow hover:bg-white disabled:opacity-40 dark:bg-slate-900/80 dark:text-rose-300 dark:hover:bg-slate-900'
              title={t('deletePage')}
              aria-label={t('deletePage')}
            >
              <Trash2 className='h-4 w-4' />
            </button>
          </div>
        )}
      </div>

      <span className='font-mono text-[11px] font-semibold text-slate-500 dark:text-slate-400'>
        {label}
      </span>
      <PageMatchLabel page={page} />

      {!readOnly && (
        <input
          ref={rescanInputRef}
          type='file'
          accept='image/*'
          className='hidden'
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void onRescan(page.id, file);
          }}
        />
      )}
    </div>
  );
}
